import "./WebsiteBuilder.css";
import React, { useContext, useEffect, useState } from "react";
import {
  AppstoreOutlined,
  ArrowRightOutlined,
  BgColorsOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  UnorderedListOutlined,
  PhoneOutlined,
  PictureOutlined,
  PlusOutlined,
  PoweroffOutlined,
  SaveOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { PageHeader } from "@ant-design/pro-layout";
import {
  Button,
  Card,
  ColorPicker,
  Col,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Spin,
  Switch,
  Tooltip,
} from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../../context/AppContextProvider";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import ImageUpload from "../../../components/ImageUpload";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import SocialMediaIcon from "../../../components/SocialMediaIcon";
import {
  getBranches,
  getWebsiteSettings,
  saveWebsiteSettings,
} from "../../../network/network";
import { handleApiError } from "../../../utils/errorHandler";
import { cleanArabicInput } from "../../../utils/validators";

const WEBSITE_BANNER_EXTENSIONS = ["png", "jpeg", "jpg", "webp", "gif"];

const WORKING_DAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const getDefaultWorkingHours = () =>
  WORKING_DAYS.map((day) => ({
    day,
    enabled: false,
    open: "",
    close: "",
  }));

const normalizeWorkingHours = (hours) => {
  if (!Array.isArray(hours)) return getDefaultWorkingHours();

  const savedHours = new Map(hours.map((item) => [item?.day, item]));
  return getDefaultWorkingHours().map((defaultDay) => {
    const savedDay = savedHours.get(defaultDay.day);

    return {
      ...defaultDay,
      enabled: Boolean(savedDay?.is_open),
      open: savedDay?.opens_at || "",
      close: savedDay?.closes_at || "",
    };
  });
};

const SOCIAL_FIELDS = [
  "instagram",
  "tiktok",
  "facebook",
  "x",
  "youtube",
  "whatsapp",
  "snapchat",
  "tripadvisor",
];

const getDefaultSettings = () => ({
  enabled: false,
  primaryColor: "#bfd508",
  defaultLanguage: "en",
  banners: [],
  about: {
    title: "",
    titleAr: "",
    description: "",
    descriptionAr: "",
    highlights: ["", "", ""],
    highlightsAr: ["", "", ""],
    stats: [
      { value: "", label: "", valueAr: "", labelAr: "" },
      { value: "", label: "", valueAr: "", labelAr: "" },
      { value: "", label: "", valueAr: "", labelAr: "" },
      { value: "", label: "", valueAr: "", labelAr: "" },
    ],
  },
  contact: {
    phone: "",
    email: "",
    hours: getDefaultWorkingHours(),
    address: "",
    addressAr: "",
  },
  social: SOCIAL_FIELDS.reduce((social, field) => {
    social[field] = "";
    return social;
  }, {}),
  branchIds: [],
  productIds: null,
});

const getProfileProducts = (user) => {
  const categories = Array.isArray(user?.categories) ? user.categories : [];

  return categories.flatMap((category) =>
    Array.isArray(category?.products) ? category.products : [],
  );
};

const mapWebsiteSettings = (website, authUser) => {
  const defaults = getDefaultSettings();
  if (!website || typeof website !== "object") return defaults;

  const about =
    website.about && typeof website.about === "object" ? website.about : {};
  const contact =
    website.contact && typeof website.contact === "object"
      ? website.contact
      : {};
  const socialLinks =
    website.social_links && typeof website.social_links === "object"
      ? website.social_links
      : {};
  const savedHighlights = Array.isArray(about.highlights)
    ? about.highlights
    : [];
  const savedHighlightsAr = Array.isArray(about.highlights_ar)
    ? about.highlights_ar
    : [];
  const savedStats = Array.isArray(about.stats) ? about.stats : [];
  const savedStatsAr = Array.isArray(about.stats_ar) ? about.stats_ar : [];

  return {
    enabled: Boolean(website.is_enabled),
    primaryColor: website.primary_color || defaults.primaryColor,
    defaultLanguage:
      String(website.default_language || "en").toLowerCase().startsWith("ar")
        ? "ar"
        : "en",
    banners: Array.isArray(website.banners)
      ? website.banners.slice(0, 3).map((banner, index) => ({
          id: banner?.id || `banner-${index}`,
          title: banner?.title || "",
          titleAr: banner?.title_ar || "",
          tag: banner?.label || "",
          tagAr: banner?.label_ar || "",
          description: banner?.description || "",
          descriptionAr: banner?.description_ar || "",
          color:
            banner?.background_color ||
            website.primary_color ||
            defaults.primaryColor,
          image: banner?.image || "",
        }))
      : defaults.banners,
    about: {
      title: about.heading || "",
      titleAr: about.heading_ar || "",
      description: about.description || "",
      descriptionAr: about.description_ar || "",
      highlights: Array.from(
        {
          length: Math.max(
            defaults.about.highlights.length,
            savedHighlights.length,
            savedHighlightsAr.length,
          ),
        },
        (_, index) => savedHighlights[index] || "",
      ),
      highlightsAr: Array.from(
        {
          length: Math.max(
            defaults.about.highlightsAr.length,
            savedHighlights.length,
            savedHighlightsAr.length,
          ),
        },
        (_, index) => savedHighlightsAr[index] || "",
      ),
      stats: Array.from(
        {
          length: Math.max(
            defaults.about.stats.length,
            savedStats.length,
            savedStatsAr.length,
          ),
        },
        (_, index) => ({
          ...(defaults.about.stats[index] || {
            value: "",
            label: "",
            valueAr: "",
            labelAr: "",
          }),
          ...(savedStats[index] || {}),
          valueAr:
            savedStatsAr[index]?.value ?? savedStats[index]?.value_ar ?? "",
          labelAr:
            savedStatsAr[index]?.label ?? savedStats[index]?.label_ar ?? "",
        }),
      ),
    },
    contact: {
      phone: contact.phone_number ?? authUser?.phone ?? "",
      email: contact.email ?? authUser?.email ?? "",
      address: contact.address ?? authUser?.business_address ?? "",
      addressAr: contact.address_ar ?? "",
      hours: normalizeWorkingHours(contact.working_hours),
    },
    social: SOCIAL_FIELDS.reduce((social, field) => {
      social[field] = socialLinks[field] || "";
      return social;
    }, {}),
    branchIds: Array.isArray(website.branch_ids) ? website.branch_ids : [],
    productIds: Array.isArray(website.product_ids) ? website.product_ids : null,
  };
};

const getBranchName = (branch, direction) => {
  if (!branch) return "";
  if (direction === "rtl") {
    return (
      branch.name_ar || branch.name_en || branch.name || branch.branch_name
    );
  }
  return branch.name_en || branch.name || branch.branch_name || branch.name_ar;
};

const getProductName = (product, direction) => {
  if (!product) return "";
  return direction === "rtl"
    ? product.name_ar || product.name || product.name_en
    : product.name_en || product.name || product.name_ar;
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result));
    reader.addEventListener("error", reject);
    reader.readAsDataURL(file);
  });

const cleanText = (value) => (typeof value === "string" ? value.trim() : "");

const getSelectedIds = (selectedIds, records) =>
  (selectedIds === null ? records.map((record) => record.id) : selectedIds)
    .filter((id) => id !== null && id !== undefined)
    .filter((id, index, ids) => ids.indexOf(id) === index);

export const buildWebsitePayload = (settings, branches, products) => {
  const highlightRows = settings.about.highlights
    .slice(0, 10)
    .map((highlight, index) => ({
      en: cleanText(highlight),
      ar: cleanText(settings.about.highlightsAr[index]),
    }))
    .filter((highlight) => highlight.en || highlight.ar);

  const statRows = settings.about.stats
    .slice(0, 6)
    .map((stat) => ({
      value: cleanText(stat.value),
      label: cleanText(stat.label),
      valueAr: cleanText(stat.valueAr),
      labelAr: cleanText(stat.labelAr),
    }))
    .filter((stat) => stat.value || stat.label || stat.valueAr || stat.labelAr);

  return {
    is_enabled: Boolean(settings.enabled),
    primary_color: settings.primaryColor,
    default_language: settings.defaultLanguage === "ar" ? "ar" : "en",
    banners: settings.banners.slice(0, 3).map((banner) => {
      const id = String(banner.id || "");

      return {
        ...(id && !id.startsWith("banner-") ? { id: banner.id } : {}),
        title: cleanText(banner.title),
        title_ar: cleanText(banner.titleAr),
        label: cleanText(banner.tag),
        label_ar: cleanText(banner.tagAr),
        description: cleanText(banner.description),
        description_ar: cleanText(banner.descriptionAr),
        background_color: banner.color || settings.primaryColor,
        image: banner.image || null,
      };
    }),
    about: {
      heading: cleanText(settings.about.title),
      heading_ar: cleanText(settings.about.titleAr),
      description: cleanText(settings.about.description),
      description_ar: cleanText(settings.about.descriptionAr),
      highlights: highlightRows.map((highlight) => highlight.en),
      highlights_ar: highlightRows.map((highlight) => highlight.ar),
      stats: statRows.map((stat) => ({
        value: stat.value,
        label: stat.label,
      })),
      stats_ar: statRows.map((stat) => ({
        value: stat.valueAr,
        label: stat.labelAr,
      })),
    },
    contact: {
      phone_number: cleanText(settings.contact.phone),
      email: cleanText(settings.contact.email),
      address: cleanText(settings.contact.address),
      address_ar: cleanText(settings.contact.addressAr),
      working_hours: WORKING_DAYS.map((day) => {
        const item =
          settings.contact.hours.find((hours) => hours.day === day) || {};

        return {
          day,
          is_open: Boolean(item.enabled),
          opens_at: item.enabled ? item.open || null : null,
          closes_at: item.enabled ? item.close || null : null,
        };
      }),
    },
    social_links: SOCIAL_FIELDS.reduce((links, field) => {
      links[field] = cleanText(settings.social[field]);
      return links;
    }, {}),
    branch_ids: getSelectedIds(settings.branchIds, branches),
    product_ids: getSelectedIds(settings.productIds, products),
  };
};

const WebsiteBuilder = () => {
  const { authUser, appConfig } = useContext(AppContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(getDefaultSettings);
  const [activeTab, setActiveTab] = useState("appearance");
  const [productView, setProductView] = useState("grid");
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [savingWebsite, setSavingWebsite] = useState(false);
  const [savingBanner, setSavingBanner] = useState(false);
  const [bannerModalOpen, setBannerModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerColor, setBannerColor] = useState("");
  const [bannerImage, setBannerImage] = useState("");
  const [bannerImageFile, setBannerImageFile] = useState(null);
  const [bannerForm] = Form.useForm();

  const direction = appConfig?.direction || "ltr";

  useEffect(() => {
    setProducts(getProfileProducts(authUser));
  }, [authUser]);

  useEffect(() => {
    let isMounted = true;

    setIsDataLoading(true);
    getWebsiteSettings()
      .then((websiteResponse) => {
        if (!isMounted) return;

        const website = websiteResponse?.data?.data?.website;

        if (!website) {
          setWebsiteUrl("");
          setSettings(mapWebsiteSettings({}, authUser));
          return;
        }

        setWebsiteUrl(website.website_url || "");
        setSettings(mapWebsiteSettings(website, authUser));
      })
      .catch((error) => {
        if (isMounted) handleApiError(error, t);
      })
      .finally(() => {
        if (isMounted) setIsDataLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [authUser, t]);

  useEffect(() => {
    let isMounted = true;
    setLoadingBranches(true);

    getBranches({ per_page: -1 })
      .then((branchesResponse) => {
        if (!isMounted) return;

        const branchData = branchesResponse?.data?.data?.branches || [];
        setBranches(Array.isArray(branchData) ? branchData : []);
      })
      .catch((error) => {
        if (isMounted) handleApiError(error, t);
      })
      .finally(() => {
        if (isMounted) setLoadingBranches(false);
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id]);

  const updateSettings = (updates) => {
    setSettings((current) => ({ ...current, ...updates }));
  };

  const updateNestedSettings = (section, updates) => {
    setSettings((current) => ({
      ...current,
      [section]: { ...current[section], ...updates },
    }));
  };

  const toggleBranchVisibility = (branchId) => {
    setSettings((current) => {
      const visibleBranchIds =
        current.branchIds === null
          ? branches.map((branch) => branch.id)
          : current.branchIds;
      const isVisible = visibleBranchIds.some(
        (id) => Number(id) === Number(branchId),
      );

      return {
        ...current,
        branchIds: isVisible
          ? visibleBranchIds.filter((id) => Number(id) !== Number(branchId))
          : [...visibleBranchIds, branchId],
      };
    });
  };

  const isBranchVisible = (branchId) =>
    settings.branchIds === null ||
    settings.branchIds.some((id) => Number(id) === Number(branchId));

  const visibleBranchCount = branches.filter((branch) =>
    isBranchVisible(branch.id),
  ).length;
  const areAllBranchesVisible =
    branches.length > 0 && visibleBranchCount === branches.length;

  const toggleAllBranches = () => {
    setSettings((current) => ({
      ...current,
      branchIds: areAllBranchesVisible
        ? []
        : branches.map((branch) => branch.id),
    }));
  };

  const saveWebsite = () => {
    const payload = buildWebsitePayload(settings, branches, products);

    setSavingWebsite(true);
    saveWebsiteSettings(payload)
      .then(async (response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        NotificationWithIcon("success", t("website_saved"));

        try {
          const websiteResponse = await getWebsiteSettings();
          const website = websiteResponse?.data?.data?.website;

          if (!website) {
            throw new Error("Website data is missing from the API response");
          }

          setWebsiteUrl(website.website_url || "");
          setSettings(mapWebsiteSettings(website, authUser));
        } catch (error) {
          handleApiError(error, t);
        }
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => setSavingWebsite(false));
  };

  const openPublishedWebsite = () => {
    if (!websiteUrl) return;

    window.open(`https://${websiteUrl}`, "_blank", "noopener,noreferrer");
  };

  const openBannerModal = (banner = null) => {
    setEditingBanner(banner);
    setBannerColor(banner?.color || settings.primaryColor);
    setBannerImage(banner?.image || "");
    setBannerImageFile(null);
    bannerForm.setFieldsValue(
      banner || {
        title: "",
        titleAr: "",
        tag: "",
        tagAr: "",
        description: "",
        descriptionAr: "",
      },
    );
    setBannerModalOpen(true);
  };

  const saveBanner = async () => {
    try {
      const values = await bannerForm.validateFields();
      setSavingBanner(true);
      const image = bannerImageFile
        ? await fileToDataUrl(bannerImageFile)
        : bannerImage;
      const bannerValues = {
        ...values,
        titleAr: cleanArabicInput(values.titleAr),
        tagAr: cleanArabicInput(values.tagAr),
        descriptionAr: cleanArabicInput(values.descriptionAr),
        color: bannerColor,
        image,
      };

      if (!editingBanner && settings.banners.length >= 3) {
        setBannerModalOpen(false);
        return;
      }

      setSettings((current) => {
        if (editingBanner) {
          return {
            ...current,
            banners: current.banners.map((banner) =>
              banner.id === editingBanner.id
                ? { ...banner, ...bannerValues }
                : banner,
            ),
          };
        }

        return {
          ...current,
          banners: [
            ...current.banners,
            { ...bannerValues, id: `banner-${Date.now()}` },
          ],
        };
      });
      setBannerModalOpen(false);
    } catch (error) {
      if (error?.errorFields) return;
      NotificationWithIcon("error", t("website_image_read_error"));
    } finally {
      setSavingBanner(false);
    }
  };

  const deleteBanner = (bannerId) => {
    setSettings((current) => ({
      ...current,
      banners: current.banners.filter((banner) => banner.id !== bannerId),
    }));
  };

  const updateHighlight = (index, value, language = "en") => {
    const key = language === "ar" ? "highlightsAr" : "highlights";
    const highlights = [...settings.about[key]];
    highlights[index] = language === "ar" ? cleanArabicInput(value) : value;
    updateNestedSettings("about", { [key]: highlights });
  };

  const addHighlight = () => {
    updateNestedSettings("about", {
      highlights: [...settings.about.highlights, ""],
      highlightsAr: [...settings.about.highlightsAr, ""],
    });
  };

  const removeHighlight = (index) => {
    updateNestedSettings("about", {
      highlights: settings.about.highlights.filter(
        (_, itemIndex) => itemIndex !== index,
      ),
      highlightsAr: settings.about.highlightsAr.filter(
        (_, itemIndex) => itemIndex !== index,
      ),
    });
  };

  const updateStat = (index, field, value) => {
    const stats = settings.about.stats.map((stat, statIndex) =>
      statIndex === index ? { ...stat, [field]: value } : stat,
    );
    updateNestedSettings("about", { stats });
  };

  const updateWorkingHours = (day, updates) => {
    updateNestedSettings("contact", {
      hours: settings.contact.hours.map((item) =>
        item.day === day ? { ...item, ...updates } : item,
      ),
    });
  };

  const isProductVisible = (productId) =>
    settings.productIds === null ||
    settings.productIds.some((id) => Number(id) === Number(productId));

  const toggleProductVisibility = (productId) => {
    setSettings((current) => {
      const visibleProductIds =
        current.productIds === null
          ? products.map((product) => product.id)
          : current.productIds;
      const isVisible = visibleProductIds.some(
        (id) => Number(id) === Number(productId),
      );

      return {
        ...current,
        productIds: isVisible
          ? visibleProductIds.filter((id) => Number(id) !== Number(productId))
          : [...visibleProductIds, productId],
      };
    });
  };

  const visibleProductCount = products.filter((product) =>
    isProductVisible(product.id),
  ).length;
  const areAllProductsVisible =
    products.length > 0 && visibleProductCount === products.length;

  const toggleAllProducts = () => {
    setSettings((current) => ({
      ...current,
      productIds: areAllProductsVisible
        ? []
        : products.map((product) => product.id),
    }));
  };

  const breadcrumbRoutes = [
    { path: "/", breadcrumbName: t("home"), icon: <ShopOutlined /> },
    {
      path: "/my-website",
      breadcrumbName: t("website"),
      icon: <GlobalOutlined />,
    },
  ];

  const tabs = [
    {
      key: "appearance",
      label: (
        <span>
          <BgColorsOutlined /> {t("website_appearance")}
        </span>
      ),
      children: (
        <div className="website-builder__section-grid">
          <Card className="website-builder__card" title={t("website_brand")}>
            <p className="website-builder__helper">{t("website_brand_help")}</p>
            <div className="website-builder__brand-control">
              <div
                className="website-builder__brand-swatch"
                style={{ background: settings.primaryColor }}
              >
                Aa
              </div>
              <div className="website-builder__brand-copy">
                <strong>{t("website_primary_colour")}</strong>
                <span>{settings.primaryColor.toUpperCase()}</span>
              </div>
              <div className="website-builder__color-picker">
                <ColorPicker
                  showText
                  format="hex"
                  value={settings.primaryColor}
                  onChange={(_, hex) => updateSettings({ primaryColor: hex })}
                />
              </div>
            </div>
            <div className="website-builder__default-language">
              <div>
                <strong>{t("website_default_language")}</strong>
                <p>{t("website_default_language_help")}</p>
              </div>
              <Space>
                <Button
                  type={settings.defaultLanguage === "en" ? "primary" : "default"}
                  onClick={() => updateSettings({ defaultLanguage: "en" })}
                >
                  {t("website_language_english")}
                </Button>
                <Button
                  type={settings.defaultLanguage === "ar" ? "primary" : "default"}
                  onClick={() => updateSettings({ defaultLanguage: "ar" })}
                >
                  {t("website_language_arabic")}
                </Button>
              </Space>
            </div>
            <div className="website-builder__tip-section">
              <InfoCircleOutlined />
              <div>
                <strong>{t("website_tip_title")}</strong>
                <p>{t("website_tip_text")}</p>
              </div>
            </div>
          </Card>
        </div>
      ),
    },
    {
      key: "banners",
      label: (
        <span>
          <PictureOutlined /> {t("website_banners")}
        </span>
      ),
      children: (
        <Card
          className="website-builder__card"
          title={t("website_hero_banners")}
          extra={
            <Button
              icon={<PlusOutlined />}
              type="primary"
              disabled={settings.banners.length >= 3}
              onClick={() => openBannerModal()}
            >
              {t("website_add_banner")}
            </Button>
          }
        >
          <p className="website-builder__helper">
            {t("website_hero_banners_help")}
          </p>
          {settings.banners.length ? (
            <div className="website-builder__banner-list">
              {settings.banners.map((banner) => (
                <div className="website-builder__banner-row" key={banner.id}>
                  <div
                    className="website-builder__banner-image"
                    style={{ background: banner.color }}
                  >
                    {banner.image ? (
                      <img src={banner.image} alt="" />
                    ) : (
                      <PictureOutlined />
                    )}
                  </div>
                  <div className="website-builder__banner-copy">
                    <strong>{banner.title || banner.titleAr}</strong>
                    <span>{banner.tag || banner.tagAr || t("website_no_label")}</span>
                  </div>
                  <Space>
                    <Tooltip title={t("edit")}>
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => openBannerModal(banner)}
                      />
                    </Tooltip>
                    <Tooltip title={t("delete_entity")}>
                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => deleteBanner(banner.id)}
                      />
                    </Tooltip>
                  </Space>
                </div>
              ))}
            </div>
          ) : (
            <Empty
              description={t("website_no_banners")}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Card>
      ),
    },
    {
      key: "about",
      label: (
        <span>
          <InfoCircleOutlined /> {t("website_about")}
        </span>
      ),
      children: (
        <Card
          className="website-builder__card"
          title={t("website_about_section")}
        >
          <p className="website-builder__helper">{t("website_about_help")}</p>
          <Form layout="vertical">
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t("website_in_english", {
                    field: t("website_section_heading"),
                  })}
                >
                  <Input
                    placeholder={t("website_section_heading_placeholder")}
                    value={settings.about.title}
                    onChange={(event) =>
                      updateNestedSettings("about", { title: event.target.value })
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t("website_in_arabic", {
                    field: t("website_section_heading"),
                  })}
                >
                  <Input
                    dir="rtl"
                    placeholder={t("website_section_heading_placeholder")}
                    value={settings.about.titleAr}
                    onChange={(event) =>
                      updateNestedSettings("about", {
                        titleAr: cleanArabicInput(event.target.value),
                      })
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t("website_in_english", {
                    field: t("website_about_text"),
                  })}
                >
                  <Input.TextArea
                    placeholder={t("website_about_text_placeholder")}
                    rows={5}
                    value={settings.about.description}
                    onChange={(event) =>
                      updateNestedSettings("about", {
                        description: event.target.value,
                      })
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t("website_in_arabic", {
                    field: t("website_about_text"),
                  })}
                >
                  <Input.TextArea
                    dir="rtl"
                    placeholder={t("website_about_text_placeholder")}
                    rows={5}
                    value={settings.about.descriptionAr}
                    onChange={(event) =>
                      updateNestedSettings("about", {
                        descriptionAr: cleanArabicInput(event.target.value),
                      })
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
            <div className="website-builder__field-group">
              <div className="website-builder__field-label">
                {t("website_highlights")}
              </div>
              <p className="website-builder__field-help">
                {t("website_highlights_help")}
              </p>
              <div className="website-builder__highlight-list">
                {settings.about.highlights.map((highlight, index) => (
                  <div
                    className="website-builder__highlight-row"
                    key={`highlight-${index}`}
                  >
                    <span className="website-builder__row-number">
                      {index + 1}
                    </span>
                    <Input
                      aria-label={`${t("website_highlight_number", {
                        number: index + 1,
                      })} - ${t("website_language_english")}`}
                      placeholder={`${t("website_highlight_placeholder")} (${t("website_language_english")})`}
                      value={highlight}
                      onChange={(event) =>
                        updateHighlight(index, event.target.value, "en")
                      }
                    />
                    <Input
                      dir="rtl"
                      aria-label={`${t("website_highlight_number", {
                        number: index + 1,
                      })} - ${t("website_language_arabic")}`}
                      placeholder={`${t("website_highlight_placeholder")} (${t("website_language_arabic")})`}
                      value={settings.about.highlightsAr[index] || ""}
                      onChange={(event) =>
                        updateHighlight(index, event.target.value, "ar")
                      }
                    />
                    <Tooltip title={t("remove")}>
                      <Button
                        aria-label={t("remove")}
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => removeHighlight(index)}
                      />
                    </Tooltip>
                  </div>
                ))}
              </div>
              <Button
                className="website-builder__add-row"
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addHighlight}
              >
                {t("website_add_highlight")}
              </Button>
            </div>
            <div className="website-builder__field-group website-builder__stats-group">
              <div className="website-builder__field-label">
                {t("website_stats")}
              </div>
              <p className="website-builder__field-help">
                {t("website_stats_help")}
              </p>
              <div className="website-builder__stats-list">
                {settings.about.stats.map((stat, index) => (
                  <div
                    className="website-builder__stat-row"
                    key={`stat-${index}`}
                  >
                    <Input
                      aria-label={`${t("website_stat_value", {
                        number: index + 1,
                      })} - ${t("website_language_english")}`}
                      placeholder={`${t("website_stat_value_placeholder")} (${t("website_language_english")})`}
                      value={stat.value}
                      onChange={(event) =>
                        updateStat(index, "value", event.target.value)
                      }
                    />
                    <Input
                      aria-label={`${t("website_stat_label", {
                        number: index + 1,
                      })} - ${t("website_language_english")}`}
                      placeholder={`${t("website_stat_label_placeholder")} (${t("website_language_english")})`}
                      value={stat.label}
                      onChange={(event) =>
                        updateStat(index, "label", event.target.value)
                      }
                    />
                    <Input
                      dir="rtl"
                      aria-label={`${t("website_stat_value", {
                        number: index + 1,
                      })} - ${t("website_language_arabic")}`}
                      placeholder={`${t("website_stat_value_placeholder")} (${t("website_language_arabic")})`}
                      value={stat.valueAr}
                      onChange={(event) =>
                        updateStat(index, "valueAr", event.target.value)
                      }
                    />
                    <Input
                      dir="rtl"
                      aria-label={`${t("website_stat_label", {
                        number: index + 1,
                      })} - ${t("website_language_arabic")}`}
                      placeholder={`${t("website_stat_label_placeholder")} (${t("website_language_arabic")})`}
                      value={stat.labelAr}
                      onChange={(event) =>
                        updateStat(index, "labelAr", cleanArabicInput(event.target.value))
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          </Form>
        </Card>
      ),
    },
    {
      key: "contact",
      label: (
        <span>
          <PhoneOutlined /> {t("website_contact")}
        </span>
      ),
      children: (
        <div className="website-builder__section-grid">
          <Card
            className="website-builder__card"
            title={t("website_contact_details")}
          >
            <Form layout="vertical">
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label={t("phone_number")}>
                    <Input
                      placeholder={t("phone_number_placeholder")}
                      value={settings.contact.phone}
                      onChange={(event) =>
                        updateNestedSettings("contact", {
                          phone: event.target.value,
                        })
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={t("email")}>
                    <Input
                      placeholder={t("email_placeholder")}
                      type="email"
                      value={settings.contact.email}
                      onChange={(event) =>
                        updateNestedSettings("contact", {
                          email: event.target.value,
                        })
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={t("website_in_english", { field: t("address") })}
                  >
                    <Input
                      placeholder={t("address_placeholder")}
                      value={settings.contact.address}
                      onChange={(event) =>
                        updateNestedSettings("contact", {
                          address: event.target.value,
                        })
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={t("website_in_arabic", { field: t("address") })}
                  >
                    <Input
                      dir="rtl"
                      placeholder={t("address_placeholder")}
                      value={settings.contact.addressAr}
                      onChange={(event) =>
                        updateNestedSettings("contact", {
                          addressAr: cleanArabicInput(event.target.value),
                        })
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
              <div className="website-builder__working-hours">
                <div className="website-builder__working-hours-heading">
                  <div className="website-builder__working-hours-icon">
                    <ClockCircleOutlined />
                  </div>
                  <div>
                    <strong>{t("website_working_hours")}</strong>
                    <span>{t("website_working_hours_help")}</span>
                  </div>
                </div>
                <div className="website-builder__hours-list">
                  {settings.contact.hours.map((item) => (
                    <div className="website-builder__hours-row" key={item.day}>
                      <strong className="website-builder__day">
                        {t(`website_day_${item.day}`)}
                      </strong>
                      <div className="website-builder__hours-status">
                        <Switch
                          size="small"
                          checked={item.enabled}
                          aria-label={t("website_toggle_day", {
                            day: t(`website_day_${item.day}`),
                          })}
                          onChange={(enabled) =>
                            updateWorkingHours(item.day, { enabled })
                          }
                        />
                        <span>
                          {item.enabled
                            ? t("website_open")
                            : t("website_closed")}
                        </span>
                      </div>
                      <div className="website-builder__time-range">
                        <Input
                          type="time"
                          disabled={!item.enabled}
                          value={item.open}
                          aria-label={t("website_open_time_for", {
                            day: t(`website_day_${item.day}`),
                          })}
                          onChange={(event) =>
                            updateWorkingHours(item.day, {
                              open: event.target.value,
                            })
                          }
                        />
                        <span>{t("website_to")}</span>
                        <Input
                          type="time"
                          disabled={!item.enabled}
                          value={item.close}
                          aria-label={t("website_close_time_for", {
                            day: t(`website_day_${item.day}`),
                          })}
                          onChange={(event) =>
                            updateWorkingHours(item.day, {
                              close: event.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Form>
          </Card>
        </div>
      ),
    },
    {
      key: "social",
      label: (
        <span>
          <LinkOutlined /> {t("website_social")}
        </span>
      ),
      children: (
        <div className="website-builder__section-grid">
          <Card className="website-builder__card" title={t("website_social")}>
            <p className="website-builder__helper">
              {t("website_social_help")}
            </p>
            <div className="website-builder__social-list">
              {[
                {
                  key: "facebook",
                  label: "facebook",
                  value: settings.social.facebook,
                  title: <SocialMediaIcon network="facebook" />,
                  className: "is-facebook",
                  placeholder: "https://facebook.com/yourpage",
                },
                {
                  key: "x",
                  label: "x",
                  value: settings.social.x,
                  title: <SocialMediaIcon network="x" />,
                  className: "is-x",
                  placeholder: "https://x.com/yourhandle",
                },
                {
                  key: "youtube",
                  label: "youtube",
                  value: settings.social.youtube,
                  title: <SocialMediaIcon network="youtube" />,
                  className: "is-youtube",
                  placeholder: "https://youtube.com/@yourchannel",
                },
                {
                  key: "instagram",
                  label: "instagram",
                  value: settings.social.instagram,
                  title: <SocialMediaIcon network="instagram" />,
                  className: "is-instagram",
                  placeholder: "https://instagram.com/yourpage",
                },
                {
                  key: "tiktok",
                  label: "tiktok",
                  value: settings.social.tiktok,
                  title: <SocialMediaIcon network="tiktok" />,
                  className: "is-tiktok",
                  placeholder: "https://tiktok.com/@yourhandle",
                },
                {
                  key: "whatsapp",
                  label: "whatsapp",
                  value: settings.social.whatsapp,
                  title: <SocialMediaIcon network="whatsapp" />,
                  className: "is-whatsapp",
                  placeholder: "https://wa.me/yourphonenumber",
                },
                {
                  key: "snapchat",
                  label: "snapchat",
                  value: settings.social.snapchat,
                  title: <SocialMediaIcon network="snapchat" />,
                  className: "is-snapchat",
                  placeholder: "https://snapchat.com/add/yourname",
                },
                {
                  key: "tripadvisor",
                  label: "tripadvisor",
                  value: settings.social.tripadvisor,
                  title: <SocialMediaIcon network="tripadvisor" />,
                  className: "is-tripadvisor",
                  placeholder: "https://tripadvisor.com/yourrestaurant",
                },
              ].map((link) => (
                <div
                  className={`website-builder__social-item ${link.className}`}
                  key={link.key}
                >
                  <div
                    className={`website-builder__social-icon ${link.className}`}
                  >
                    {link.title}
                  </div>
                  <Input
                    placeholder={link.placeholder}
                    value={link.value || undefined}
                    onChange={(event) =>
                      updateNestedSettings("social", {
                        [link.key]: event.target.value,
                      })
                    }
                  />
                </div>
              ))}
            </div>
          </Card>
        </div>
      ),
    },
    {
      key: "branches",
      label: (
        <span>
          <EnvironmentOutlined /> {t("website_branches")}
        </span>
      ),
      children: (
        <Card
          className="website-builder__card"
          title={t("website_online_branches")}
          extra={
            <Button type="link" onClick={() => navigate("/branches")}>
              {t("website_manage_branches")} <ArrowRightOutlined />
            </Button>
          }
        >
          <p className="website-builder__helper">
            {t("website_branches_help")}
          </p>
          {loadingBranches ? (
            <div className="website-builder__loading">
              <Spin />
            </div>
          ) : branches.length ? (
            <div className="website-builder__branch-preview">
              <div className="website-builder__branch-notice">
                <div className="website-builder__menu-summary-icon">
                  <ShopOutlined />
                </div>
                <div>
                  <strong>{t("website_all_branches")}</strong>
                  <span>{t("website_branch_visibility_help")}</span>
                </div>
                <span className="website-builder__visible-count">
                  {visibleBranchCount} / {branches.length}
                </span>
                <Button
                  className="website-builder__bulk-branch-action"
                  onClick={toggleAllBranches}
                  size="small"
                >
                  {areAllBranchesVisible
                    ? t("website_hide_all_branches")
                    : t("website_show_all_branches")}
                </Button>
              </div>

              <div className="website-builder__branch-grid">
                {branches.map((branch) => {
                  const isVisible = isBranchVisible(branch.id);

                  return (
                    <article
                      className={`website-builder__branch-card ${
                        isVisible ? "is-visible" : ""
                      }`}
                      key={branch.id}
                    >
                      <div className="website-builder__branch-icon">
                        <ShopOutlined />
                      </div>
                      <div className="website-builder__branch-copy">
                        <strong>{getBranchName(branch, direction)}</strong>
                        <span>
                          {branch.business_address || branch.address || "—"}
                        </span>
                      </div>
                      <div className="website-builder__visibility-control">
                        <span>
                          {isVisible
                            ? t("website_visible")
                            : t("website_hidden")}
                        </span>
                        <Switch
                          checked={isVisible}
                          aria-label={t("website_toggle_branch", {
                            branch: getBranchName(branch, direction),
                          })}
                          onChange={() => toggleBranchVisibility(branch.id)}
                        />
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          ) : (
            <Empty
              description={t("website_no_branches")}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Card>
      ),
    },
    {
      key: "menu",
      label: (
        <span>
          <AppstoreOutlined /> {t("website_menu")}
        </span>
      ),
      children: (
        <Card
          className="website-builder__card"
          title={t("website_menu_visibility")}
          extra={
            <Button type="link" onClick={() => navigate("/products")}>
              {t("website_manage_products")} <ArrowRightOutlined />
            </Button>
          }
        >
          {products.length ? (
            <div className="website-builder__menu-preview">
              <div className="website-builder__menu-notice">
                <div className="website-builder__menu-summary-icon">
                  <AppstoreOutlined />
                </div>
                <div>
                  <strong>{t("website_all_products")}</strong>
                  <span>{t("website_product_visibility_help")}</span>
                </div>
                <span className="website-builder__visible-count">
                  {visibleProductCount} / {products.length}
                </span>
                <Button
                  className="website-builder__bulk-product-action"
                  onClick={toggleAllProducts}
                  size="small"
                >
                  {areAllProductsVisible
                    ? t("website_hide_all_products")
                    : t("website_show_all_products")}
                </Button>
                <div className="website-builder__product-view-toggle">
                  <Tooltip title={t("website_grid_view")}>
                    <Button
                      aria-label={t("website_grid_view")}
                      className={productView === "grid" ? "is-active" : ""}
                      icon={<AppstoreOutlined />}
                      onClick={() => setProductView("grid")}
                      type="text"
                    />
                  </Tooltip>
                  <Tooltip title={t("website_list_view")}>
                    <Button
                      aria-label={t("website_list_view")}
                      className={productView === "list" ? "is-active" : ""}
                      icon={<UnorderedListOutlined />}
                      onClick={() => setProductView("list")}
                      type="text"
                    />
                  </Tooltip>
                </div>
              </div>

              <div
                className={`website-builder__product-grid is-${productView}`}
              >
                {products.map((product) => {
                  const isVisible = isProductVisible(product.id);

                  return (
                    <article
                      className={`website-builder__product-card ${
                        isVisible ? "is-visible" : "is-hidden"
                      }`}
                      key={product.id}
                    >
                      <div className="website-builder__product-image">
                        <img
                          src={product.image || "/images/image-not-found.png"}
                          alt={getProductName(product, direction)}
                        />
                      </div>

                      <div className="website-builder__product-copy">
                        <strong>{getProductName(product, direction)}</strong>
                        <CurrencyFormatter value={product.price} />
                      </div>

                      <div className="website-builder__product-visibility">
                        <span>
                          {isVisible
                            ? t("website_visible")
                            : t("website_hidden")}
                        </span>
                        <Switch
                          checked={isVisible}
                          aria-label={t("website_toggle_product", {
                            product: getProductName(product, direction),
                          })}
                          onChange={() => toggleProductVisibility(product.id)}
                        />
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          ) : (
            <Empty
              description={t("website_no_products")}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Card>
      ),
    },
  ];

  return (
    <Spin spinning={isDataLoading}>
      <div className="website-builder" dir={direction}>
        <Card className="website-builder__header-card">
          <CustomBreadcrumb menus={breadcrumbRoutes} />
          <PageHeader
            className="app-p-0"
            title={t("website")}
            ghost={false}
            extra={[
              <Button
                key="save"
                type="primary"
                icon={<SaveOutlined />}
                loading={savingWebsite}
                onClick={saveWebsite}
              >
                {t("save")}
              </Button>,
              settings.enabled && websiteUrl && (
                <Button
                  key="preview"
                  icon={<EyeOutlined />}
                  onClick={openPublishedWebsite}
                >
                  {t("preview")}
                </Button>
              ),
            ].filter(Boolean)}
          />
        </Card>

        <Card
          className={`website-builder__status ${settings.enabled ? "is-live" : "is-offline"}`}
        >
          <div className="website-builder__status-icon">
            {settings.enabled ? <CheckCircleFilled /> : <PoweroffOutlined />}
          </div>
          <div className="website-builder__status-copy">
            <strong>
              {settings.enabled ? t("website_enabled") : t("website_disabled")}
            </strong>
            {settings.enabled && websiteUrl && (
              <a
                href={`https://${websiteUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="website-builder__status-url"
              >
                {websiteUrl}
              </a>
            )}
          </div>
          <div className="website-builder__status-switch">
            <span>{settings.enabled ? t("website_on") : t("website_off")}</span>
            <Switch
              checked={settings.enabled}
              onChange={(enabled) => updateSettings({ enabled })}
            />
          </div>
        </Card>

        <div
          className="website-builder__tab-list"
          role="tablist"
          aria-label={t("website_builder_sections")}
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key;

            return (
              <button
                key={tab.key}
                id={`website-builder-tab-${tab.key}`}
                type="button"
                role="tab"
                aria-controls={`website-builder-panel-${tab.key}`}
                aria-selected={isActive}
                className={`website-builder__tab ${isActive ? "is-active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div
          id={`website-builder-panel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`website-builder-tab-${activeTab}`}
        >
          {tabs.find((tab) => tab.key === activeTab)?.children}
        </div>

        <Modal
          rootClassName="website-builder-modal"
          title={
            editingBanner ? t("website_edit_banner") : t("website_add_banner")
          }
          open={bannerModalOpen}
          onCancel={() => setBannerModalOpen(false)}
          onOk={saveBanner}
          okText={t("save")}
          confirmLoading={savingBanner}
          destroyOnClose
          width={900}
        >
          <Form form={bannerForm} layout="vertical">
            <div className="website-builder__banner-language-grid">
              <div className="website-builder__banner-language-panel">
                <div className="website-builder__banner-language-heading">
                  <strong>{t("website_language_english")}</strong>
                  <span>EN</span>
                </div>
                <Form.Item
                  name="title"
                  label={t("website_in_english", {
                    field: t("website_banner_title"),
                  })}
                  rules={[
                    {
                      required: true,
                      message: t("website_banner_title_required"),
                    },
                  ]}
                >
                  <Input placeholder={t("website_banner_title_placeholder")} />
                </Form.Item>
                <Form.Item
                  name="tag"
                  label={t("website_in_english", {
                    field: t("website_banner_label"),
                  })}
                >
                  <Input placeholder={t("website_banner_label_placeholder")} />
                </Form.Item>
                <Form.Item
                  name="description"
                  label={t("website_in_english", { field: t("description") })}
                >
                  <Input.TextArea
                    placeholder={t("website_banner_description_placeholder")}
                    rows={5}
                  />
                </Form.Item>
              </div>

              <div className="website-builder__banner-language-panel is-arabic">
                <div className="website-builder__banner-language-heading">
                  <strong>{t("website_language_arabic")}</strong>
                  <span>AR</span>
                </div>
                <Form.Item
                  name="titleAr"
                  label={t("website_in_arabic", {
                    field: t("website_banner_title"),
                  })}
                  normalize={cleanArabicInput}
                >
                  <Input
                    dir="rtl"
                    placeholder={t("website_arabic_only_validation")}
                  />
                </Form.Item>
                <Form.Item
                  name="tagAr"
                  label={t("website_in_arabic", {
                    field: t("website_banner_label"),
                  })}
                  normalize={cleanArabicInput}
                >
                  <Input
                    dir="rtl"
                    placeholder={t("website_arabic_only_validation")}
                  />
                </Form.Item>
                <Form.Item
                  name="descriptionAr"
                  label={t("website_in_arabic", { field: t("description") })}
                  normalize={cleanArabicInput}
                >
                  <Input.TextArea
                    dir="rtl"
                    placeholder={t("website_arabic_only_validation")}
                    rows={5}
                  />
                </Form.Item>
              </div>
            </div>

            <div className="website-builder__banner-assets">
              <Form.Item label={t("website_background_colour")}>
                <div className="website-builder__color-picker">
                  <ColorPicker
                    showText
                    format="hex"
                    value={bannerColor}
                    onChange={(_, hex) => setBannerColor(hex)}
                  />
                </div>
              </Form.Item>
              <Form.Item
                label={t("website_banner_image")}
                extra={t("max_upload_size_2mb_warning")}
              >
                <div className="website-builder__banner-upload">
                  <ImageUpload
                    name="banner_image"
                    image={bannerImage}
                    allowedExtensions={WEBSITE_BANNER_EXTENSIONS}
                    callbackSetImageFile={(file) => {
                      setBannerImageFile(file);
                      if (!file) setBannerImage("");
                    }}
                  />
                </div>
              </Form.Item>
            </div>
          </Form>
        </Modal>
      </div>
    </Spin>
  );
};

export default WebsiteBuilder;
